import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail, formatEuro } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { Plus, Calendar, Clock, Ticket, FileSpreadsheet, ScanLine, Trash2, Pencil, BarChart3, RotateCcw } from "lucide-react";

function ShowFormModal({ open, onClose, onSaved, initial }) {
  const isEdit = !!initial;
  const [title, setTitle] = useState(initial?.title || "");
  const [date, setDate] = useState(initial?.date || "");
  const [time, setTime] = useState(initial?.time || "19:30");
  const [startCash, setStartCash] = useState(initial?.start_cash ?? 200);
  const [notes, setNotes] = useState(initial?.notes || "");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(initial?.title || "");
      setDate(initial?.date || "");
      setTime(initial?.time || "19:30");
      setStartCash(initial?.start_cash ?? 200);
      setNotes(initial?.notes || "");
      setErr("");
    }
  }, [open, initial]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      const body = { title, date, time, start_cash: Number(startCash) || 0, notes };
      if (isEdit) await api.put(`/shows/${initial.id}`, body);
      else await api.post("/shows", body);
      onSaved();
      onClose();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit} className="p-8 space-y-5">
          <h2 className="text-2xl font-black tracking-tight">
            {isEdit ? "Vorstellung bearbeiten" : "Neue Vorstellung"}
          </h2>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2 block">Titel</label>
            <input data-testid="show-form-title" value={title} onChange={(e) => setTitle(e.target.value)} required
              className="w-full h-12 px-3 text-base border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-950" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2 block">Datum</label>
              <input data-testid="show-form-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required
                className="w-full h-12 px-3 text-base border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-950" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2 block">Uhrzeit</label>
              <input data-testid="show-form-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} required
                className="w-full h-12 px-3 text-base border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-950" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2 block">
              Anfangs-Wechselgeld (€)
            </label>
            <input data-testid="show-form-startcash" type="number" min="0" step="0.01" value={startCash}
              onChange={(e) => setStartCash(e.target.value)}
              className="w-full h-12 px-3 text-base border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-950" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2 block">Notiz</label>
            <textarea data-testid="show-form-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="w-full px-3 py-2 text-base border border-zinc-300 rounded-md focus:outline-none focus:ring-2 focus:ring-zinc-950" />
          </div>
          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-4 py-3">{err}</div>}
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={onClose}
              className="h-12 px-5 rounded-md border border-zinc-200 hover:bg-zinc-50 font-semibold">
              Abbrechen
            </button>
            <button data-testid="show-form-save" type="submit" disabled={saving}
              className="h-12 px-6 rounded-md bg-zinc-950 hover:bg-zinc-800 text-white font-bold disabled:opacity-60">
              {saving ? "Speichern…" : "Speichern"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ShowsPage() {
  const [shows, setShows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, initial: null });
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/shows");
      setShows(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const removeShow = async (id) => {
    if (!window.confirm("Vorstellung und alle Daten unwiderruflich löschen?")) return;
    await api.delete(`/shows/${id}`);
    load();
  };

  const resetShow = async (id) => {
    if (!window.confirm("Alle Reservierungen und Transaktionen dieser Vorstellung löschen?\n\nDie Vorstellung selbst (Datum, Uhrzeit, Wechselgeld) bleibt erhalten.")) return;
    const { data } = await api.post(`/shows/${id}/reset`);
    window.alert(`Zurückgesetzt: ${data.reservations_deleted} Reservierungen und ${data.transactions_deleted} Transaktionen gelöscht.`);
    load();
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <AppHeader
        title="Vorstellungen"
        right={
          <>
            <button
              data-testid="season-btn"
              onClick={() => navigate("/season")}
              className="h-11 px-4 rounded-md border border-zinc-200 hover:bg-zinc-50 font-bold flex items-center gap-2 text-sm"
            >
              <BarChart3 className="h-4 w-4" /> Saison-Statistik
            </button>
            <button
              data-testid="new-show-btn"
              onClick={() => setModal({ open: true, initial: null })}
              className="h-11 px-4 rounded-md bg-zinc-950 hover:bg-zinc-800 text-white font-bold flex items-center gap-2 text-sm"
            >
              <Plus className="h-4 w-4" /> Neue Vorstellung
            </button>
          </>
        }
      />
      <main className="max-w-7xl mx-auto px-6 py-10">
        {loading ? (
          <p className="text-zinc-500">Lade…</p>
        ) : shows.length === 0 ? (
          <div data-testid="empty-shows" className="bg-white border border-dashed border-zinc-300 rounded-xl p-16 text-center">
            <p className="text-2xl font-bold mb-2">Noch keine Vorstellung</p>
            <p className="text-zinc-500 mb-6">Lege deine erste Vorstellung an, um Reservierungen und Kassa zu nutzen.</p>
            <button onClick={() => setModal({ open: true, initial: null })}
              className="h-12 px-6 rounded-md bg-zinc-950 hover:bg-zinc-800 text-white font-bold inline-flex items-center gap-2">
              <Plus className="h-4 w-4" /> Erste Vorstellung anlegen
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {shows.map((s) => (
              <ShowCard key={s.id} show={s} onEdit={() => setModal({ open: true, initial: s })} onDelete={() => removeShow(s.id)} onReset={() => resetShow(s.id)} onOpen={() => navigate(`/shows/${s.id}/kassa`)} />
            ))}
          </div>
        )}
      </main>
      <ShowFormModal open={modal.open} initial={modal.initial} onClose={() => setModal({ open: false, initial: null })} onSaved={load} />
    </div>
  );
}

function ShowCard({ show, onEdit, onDelete, onReset, onOpen }) {
  const navigate = useNavigate();
  return (
    <div data-testid={`show-card-${show.id}`} className="bg-white border border-zinc-200 rounded-xl shadow-sm overflow-hidden flex flex-col">
      <div className="p-6 border-b border-zinc-100">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="text-2xl font-black tracking-tight text-zinc-950 leading-tight">{show.title}</h3>
            <div className="mt-2 flex items-center gap-4 text-sm text-zinc-500">
              <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4" />{show.date}</span>
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" />{show.time}</span>
            </div>
          </div>
          {show.closed && (
            <span className="text-[10px] uppercase tracking-widest font-bold bg-zinc-950 text-white px-2 py-1 rounded">
              Abgeschlossen
            </span>
          )}
        </div>
        <p className="mt-4 text-xs uppercase tracking-[0.2em] font-bold text-zinc-500">Anfangs-Wechselgeld</p>
        <p className="font-mono text-2xl font-bold tabular-nums">{formatEuro(show.start_cash)}</p>
      </div>
      <div className="p-3 grid grid-cols-3 gap-2 mt-auto">
        <button
          data-testid={`show-reservations-${show.id}`}
          onClick={() => navigate(`/shows/${show.id}/reservations`)}
          className="h-14 rounded-md border border-zinc-200 hover:bg-zinc-50 font-semibold text-sm flex flex-col items-center justify-center gap-1"
        >
          <Ticket className="h-4 w-4" /> Reservierungen
        </button>
        <button
          data-testid={`show-kassa-${show.id}`}
          onClick={onOpen}
          className="h-14 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm flex flex-col items-center justify-center gap-1"
        >
          <ScanLine className="h-4 w-4" /> Kassa öffnen
        </button>
        <button
          data-testid={`show-abschluss-${show.id}`}
          onClick={() => navigate(`/shows/${show.id}/abschluss`)}
          className="h-14 rounded-md border border-zinc-200 hover:bg-zinc-50 font-semibold text-sm flex flex-col items-center justify-center gap-1"
        >
          <FileSpreadsheet className="h-4 w-4" /> Abschluss
        </button>
      </div>
      <div className="px-3 pb-3 flex gap-2">
        <button data-testid={`show-edit-${show.id}`} onClick={onEdit}
          className="flex-1 h-10 rounded-md border border-zinc-200 hover:bg-zinc-50 text-xs font-semibold flex items-center justify-center gap-1.5">
          <Pencil className="h-3.5 w-3.5" /> Bearbeiten
        </button>
        <button data-testid={`show-reset-${show.id}`} onClick={onReset}
          className="h-10 px-3 rounded-md border border-amber-200 text-amber-800 hover:bg-amber-50 text-xs font-semibold flex items-center justify-center gap-1.5"
          title="Reservierungen + Transaktionen löschen, Vorstellung behalten">
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
        <button data-testid={`show-delete-${show.id}`} onClick={onDelete}
          className="h-10 px-3 rounded-md border border-red-200 text-red-700 hover:bg-red-50 text-xs font-semibold flex items-center justify-center gap-1.5"
          title="Komplett löschen">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
